import React, { useState, useEffect } from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { classNames } from 'utils/styleUtils';
import InformationTooltip from 'components/InformationTooltip';

/**
 * Predefined Business Use Case options for the AstraZeneca Flow.
 * Extracted from DataProductDefinitionStep.tsx (previously inline).
 */
export const BUSINESS_USE_CASE_OPTIONS: string[] = [
  'Advanced CPV',
  'AERA',
  'APA Cognite',
  'APSD Dashboard',
  'APSR',
  'Asset Digital Twin',
  'Asset Intelligence',
  'Batch Release Visualisation',
  'Blending Model',
  'CDC Multi-Agent System',
  'ChemDev Multi-Agent System',
  'Chemical Development',
  'Chemical Development Knowledge Graph',
  'Clinical Supply Chain - Data Driven Decision Making',
  'Coating Model',
  'Cognite Asset Twin',
  'CPV Reporting - Sweden Respiratory',
  'Data Quality Dashboard',
  'Demand Day Dashboard',
  'Demand Month Dashboard',
  'Digital Supply Maps',
  'Digital Tier',
  'Digital Twin',
  'Direct Materials Dashboard',
  'DVMI Dashboard',
  'Executive Dashboard',
  'Expedite Optimizer',
  'Expedite Optimizer Dashboard',
  'External Reporting (Workiva)',
  'External Sales Forecast',
  'External Supply Manufacturing',
  'Factory Flow Visualisation (Wuxi)',
  'Factory Foundations',
  'Feeder Model',
  'FG Safety Stock',
  'Forecasting dashboard',
  'Formulation Composition Knowledge Graph',
  'Foundational Data Product',
  'Freight S&OP',
  'GenAI AI Assisted Business Review',
  'GenAI CDC Assistant',
  'GenAI Kinetics Web App',
  'GenAI PTK - Zoladex',
  'Global Product Development',
  'Golden Batch Dashboard',
  'GPD Equipment Booking App',
  'GSC - Sales',
  'GSC Analytics',
  'GSC Sustainability',
  'Insights Agent',
  'Instrument Performance Dashboard',
  'Instrument Utilisation Dashboard',
  'Integrated Inventory Reporting',
  'KPI Site Supply Dashboard',
  'Logistics Planning',
  'Macc Production Data Dashboard',
  'Material Design Knowledge Graph',
  'Material Management',
  'Method SSt Performance Monitoring Dashboard',
  'MtV Cost Dashboard',
  'NPI Automation program',
  'NPI Nova',
  'Operations Risk Management Portal',
  'Particle Engineering Dashboard',
  'Particle Size Distribution Dashboard',
  'Performance Dashboard',
  'Persona Based Dashboard',
  'Physical Properties Passport',
  'Process Intelligence',
  'Process Manager',
  'Project 44',
  'Procurement Spend Dashboard',
  'PT&D',
  'PT&D Dashboards',
  'PT&D Knowledge Graphs',
  'Resilinc',
  'SAC-Planning',
  'Sales Budgets',
  'Single View Dashboard',
  'SMART Metering',
  'Solution Prep Dashboard',
  'Stability Dashboard',
  'Sterile Settle Plate Tool',
  'Stock Out',
  'Supply Chain Chat',
  'Sustainability Assistant',
  'Sustainability Control Tower',
  'Temperature Excursions Dashboard',
  'TOSI',
  'Transport Lead Time Dashboard',
  'Transportation Lead Time',
  'Transportation Mode Adjustment',
  'Tulip',
  'Vendor OTIF',
  'Virtual Tablet Designer',
  'Yield',
  'Zoladex Yield Dashboard',
];

/** Synthetic token prepended to custom "Add:" option values in the Combobox. */
const ADD_PREFIX = '__ADD__:';

/**
 * Values that must never be selectable, even as custom "Add:" entries.
 * Stored in lower-case for case-insensitive comparison.
 */
const BLOCKED_VALUES = ['other'];

interface UseCaseMultiSelectBaseProps {
  /**
   * Values to exclude from the available options list (e.g. use cases already added
   * as pairs). Filtered out both from the predefined list and from the "Add:" option.
   * Case-sensitive comparison against predefined options; case-insensitive against the
   * typed query for the "Add:" guard.
   */
  excludeValues?: string[];
}

interface UseCaseMultiSelectMultiProps extends UseCaseMultiSelectBaseProps {
  /** Mode: 'multi' (default) — emits a comma-separated string of all selected values. */
  mode?: 'multi';
  /** Comma-separated string value from Redux (e.g. "Analytics, Reporting"). */
  value: string;
  /** Called with the new comma-separated string whenever the selection changes. */
  onChange: (value: string) => void;
}

interface UseCaseMultiSelectSingleProps extends UseCaseMultiSelectBaseProps {
  /** Mode: 'single' — emits a single selected value string (or empty string when cleared). */
  mode: 'single';
  /** The currently selected value (single string, not CSV). Empty string means nothing selected. */
  value: string;
  /** Called with the newly selected single value, or empty string when selection is cleared. */
  onChange: (value: string) => void;
}

export type UseCaseMultiSelectProps = UseCaseMultiSelectMultiProps | UseCaseMultiSelectSingleProps;

/**
 * Searchable combobox for the Business Use Case field.
 *
 * Supports two modes via the `mode` prop:
 *
 * **Multi-select mode** (`mode="multi"`, the default):
 * - Accepts/emits a comma-separated string to stay compatible with the Redux
 *   `businessUseCase: string` state and the backend `business_use_case` field.
 * - Supports selecting multiple predefined options.
 * - Allows typing a free-text custom value and adding it via the "Add: <query>" option.
 * - Selected values appear as removable chips below the input.
 * - Backward-compatible: when loading existing metadata, the stored comma-separated
 *   string is split and predefined values are pre-checked; non-predefined values appear
 *   as custom selected entries.
 *
 * **Single-select mode** (`mode="single"`):
 * - Accepts/emits a single string value (not comma-separated).
 * - Only one value can be selected at a time; selecting a new value replaces the previous one.
 * - No chips are rendered — the selected value is shown in the input field.
 * - Keeps the searchable combobox UI and "Add: <query>" custom option capability.
 *
 * **Filtering already-added values** (`excludeValues` prop):
 * - Pass an array of values to hide from the available options.
 * - Useful in single-select mode to prevent duplicates in a pair list.
 * - The "Add:" option is also suppressed when the typed query matches an excluded value.
 */
export default function UseCaseMultiSelect(props: UseCaseMultiSelectProps): JSX.Element {
  const isSingleMode = props.mode === 'single';
  const excludeSet = new Set<string>(props.excludeValues ?? []);

  // ── Multi-select state ────────────────────────────────────────────────────────

  /**
   * Parse the incoming comma-separated string into an array of trimmed, non-empty tokens.
   * Only used in multi-select mode.
   */
  const parseValue = (csv: string): string[] =>
    csv
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

  const [selectedValues, setSelectedValues] = useState<string[]>(() => (isSingleMode ? [] : parseValue(props.value)));

  // Sync internal multi-select state when the Redux value changes externally.
  useEffect(() => {
    if (!isSingleMode) {
      setSelectedValues(parseValue(props.value));
    }
  }, [props.value, isSingleMode]);

  // ── Single-select state ───────────────────────────────────────────────────────

  const [singleSelected, setSingleSelected] = useState<string | null>(() =>
    isSingleMode && props.value.length > 0 ? props.value : null,
  );

  // Sync internal single-select state when the external value changes.
  useEffect(() => {
    if (isSingleMode) {
      setSingleSelected(props.value.length > 0 ? props.value : null);
    }
  }, [props.value, isSingleMode]);

  // ── Shared query state ────────────────────────────────────────────────────────

  const [query, setQuery] = useState<string>('');

  // ── Multi-select helpers ──────────────────────────────────────────────────────

  /**
   * Emit the updated multi-select selection back as a comma-separated string.
   * Deduplicates and trims tokens before joining.
   */
  const emitChange = (newValues: string[]): void => {
    const deduplicated = Array.from(new Set(newValues.map((v) => v.trim()).filter((v) => v.length > 0)));
    props.onChange(deduplicated.join(', '));
    setSelectedValues(deduplicated);
  };

  /**
   * Remove a specific value from the current multi-select selection.
   */
  const removeValue = (valueToRemove: string): void => {
    emitChange(selectedValues.filter((v) => v !== valueToRemove));
  };

  /**
   * Called by Combobox in multi-select mode when an option is selected or deselected.
   * With `multiple`, Headless UI passes the full new array of selected values.
   * We intercept "Add:" synthetic options and resolve them to their real text.
   */
  const handleMultiChange = (newSelection: string[]): void => {
    const resolved = newSelection.map((v) => (v.startsWith(ADD_PREFIX) ? v.slice(ADD_PREFIX.length) : v));
    emitChange(resolved);
    setQuery('');
  };

  // ── Single-select handler ─────────────────────────────────────────────────────

  /**
   * Called by Combobox in single-select mode when an option is clicked.
   * Resolves "Add:" synthetic tokens and emits the single selected value.
   */
  const handleSingleChange = (value: string | null): void => {
    if (value === null) {
      setSingleSelected(null);
      props.onChange('');
    } else {
      const resolved = value.startsWith(ADD_PREFIX) ? value.slice(ADD_PREFIX.length) : value;
      setSingleSelected(resolved);
      props.onChange(resolved);
    }
    setQuery('');
  };

  // ── Shared option filtering ───────────────────────────────────────────────────

  // Build the filtered predefined options list, excluding already-added values.
  const baseOptions = BUSINESS_USE_CASE_OPTIONS.filter((o) => !excludeSet.has(o));
  const filteredOptions =
    query === '' ? baseOptions : baseOptions.filter((option) => option.toLowerCase().includes(query.toLowerCase()));

  // Determine whether to show the "Add: <query>" synthetic option.
  const queryTrimmed = query.trim();
  const queryIsBlocked = BLOCKED_VALUES.includes(queryTrimmed.toLowerCase());
  const queryContainsComma = queryTrimmed.includes(',');
  const queryMatchesPredefined = BUSINESS_USE_CASE_OPTIONS.some((o) => o.toLowerCase() === queryTrimmed.toLowerCase());
  const queryIsExcluded = [...excludeSet].some((v) => v.toLowerCase() === queryTrimmed.toLowerCase());

  // In multi-select mode, also block if the query is already in the current selection.
  const queryAlreadySelected = !isSingleMode
    ? selectedValues.some((v) => v.toLowerCase() === queryTrimmed.toLowerCase())
    : singleSelected !== null && singleSelected.toLowerCase() === queryTrimmed.toLowerCase();

  const showAddOption =
    queryTrimmed.length > 0 &&
    !queryAlreadySelected &&
    !queryMatchesPredefined &&
    !queryIsBlocked &&
    !queryContainsComma &&
    !queryIsExcluded;

  // ── Render ────────────────────────────────────────────────────────────────────

  if (isSingleMode) {
    // Single-select: Headless UI Combobox with a non-array value.
    // The value passed to Combobox is the synthetic token for custom values so
    // Headless UI can track it as "selected".
    const comboboxSingleValue: string | null =
      singleSelected !== null && !BUSINESS_USE_CASE_OPTIONS.includes(singleSelected)
        ? `${ADD_PREFIX}${singleSelected}`
        : singleSelected;

    return (
      <div className="w-full" data-testid="use-case-multi-select">
        <Combobox value={comboboxSingleValue} onChange={handleSingleChange}>
          <Label className="text-sm font-medium leading-6 text-gray-900 flex items-center">
            Business Use Case*
            <InformationTooltip tooltip="Select a use case to add a use case / program pair" />
          </Label>

          <div className="relative mt-2 w-full cursor-default rounded-md bg-white text-left shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-gray-200 sm:text-sm sm:leading-6">
            <ComboboxInput
              className="w-full py-1.5 pl-1.5 pr-10 text-black bg-transparent border-none text-sm focus:outline-none rounded-md"
              onChange={(event) => setQuery(event.target.value)}
              displayValue={(val: string | null) => {
                // While typing, show the query. When idle, show the resolved selected value.
                if (query !== '') return query;
                if (val === null) return '';
                return val.startsWith(ADD_PREFIX) ? val.slice(ADD_PREFIX.length) : val;
              }}
              placeholder="Select a use case…"
              aria-label="Business Use Case"
              data-testid="use-case-single-select-input"
            />
            <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer">
              <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </ComboboxButton>
          </div>

          <ComboboxOptions
            anchor="bottom"
            transition
            className={classNames(
              'z-50 mt-1 w-[var(--input-width)] max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
              'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
            )}
          >
            {filteredOptions.map((option) => (
              <ComboboxOption
                key={option}
                value={option}
                className="group relative cursor-default select-none py-1 pl-3 pr-9 data-[focus]:bg-gray-50"
              >
                {({ selected }) => (
                  <>
                    <span
                      className={classNames(
                        selected ? 'font-semibold text-gray-900' : 'text-gray-700',
                        'block truncate text-sm',
                      )}
                    >
                      {option}
                    </span>
                    {selected && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600">
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </ComboboxOption>
            ))}

            {showAddOption && (
              <ComboboxOption
                value={`${ADD_PREFIX}${queryTrimmed}`}
                className="group relative cursor-default select-none py-1 pl-3 pr-9 data-[focus]:bg-gray-50 border-t border-gray-100"
              >
                <span className="block truncate text-sm text-dataops-primary-blue font-medium">
                  Add: &ldquo;{queryTrimmed}&rdquo;
                </span>
              </ComboboxOption>
            )}

            {filteredOptions.length === 0 && !showAddOption && (
              <div className="relative cursor-default select-none py-2 px-4 text-sm text-gray-500">
                No options match your search.
              </div>
            )}
          </ComboboxOptions>
        </Combobox>
      </div>
    );
  }

  // ── Multi-select render ───────────────────────────────────────────────────────

  // The value passed to Combobox must contain the synthetic tokens for currently selected
  // custom values (non-predefined) so Headless UI can track them as "selected".
  const comboboxValue: string[] = selectedValues.map((v) =>
    BUSINESS_USE_CASE_OPTIONS.includes(v) ? v : `${ADD_PREFIX}${v}`,
  );

  return (
    <div className="w-full" data-testid="use-case-multi-select">
      <Combobox value={comboboxValue} onChange={handleMultiChange} multiple>
        <Label className="text-sm font-medium leading-6 text-gray-900 flex items-center">
          Business Use Case*
          <InformationTooltip tooltip="Enter new use case if not available without commas in between" />
        </Label>

        {/* Trigger button — input for typing search queries */}
        <div className="relative mt-2 w-full cursor-default rounded-md bg-white text-left shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-gray-200 sm:text-sm sm:leading-6">
          <ComboboxInput
            className="w-full py-1.5 pl-1.5 pr-10 text-black bg-transparent border-none text-sm focus:outline-none rounded-md"
            onChange={(event) => setQuery(event.target.value)}
            displayValue={() => query}
            placeholder={selectedValues.length === 0 ? 'Select use cases…' : 'Add more…'}
            aria-label="Business Use Case"
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2 cursor-pointer">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </ComboboxButton>
        </div>

        {/* Selected value chips shown below the input */}
        {selectedValues.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1" data-testid="selected-chips">
            {selectedValues.map((v) => (
              <span
                key={v}
                className="inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200"
              >
                {v}
                <button
                  type="button"
                  aria-label={`Remove ${v}`}
                  onClick={() => removeValue(v)}
                  className="ml-0.5 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <XMarkIcon className="h-3 w-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Dropdown options */}
        <ComboboxOptions
          anchor="bottom"
          transition
          className={classNames(
            'z-50 mt-1 w-[var(--input-width)] max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
            'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
          )}
        >
          {/* Predefined options */}
          {filteredOptions.map((option) => (
            <ComboboxOption
              key={option}
              value={option}
              className="group relative cursor-default select-none py-1 pl-3 pr-9 data-[focus]:bg-gray-50"
            >
              {({ selected }) => (
                <>
                  <span
                    className={classNames(
                      selected ? 'font-semibold text-gray-900' : 'text-gray-700',
                      'block truncate text-sm',
                    )}
                  >
                    {option}
                  </span>
                  {selected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600">
                      <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </ComboboxOption>
          ))}

          {/* Synthetic "Add: <query>" option for custom values */}
          {showAddOption && (
            <ComboboxOption
              value={`${ADD_PREFIX}${queryTrimmed}`}
              className="group relative cursor-default select-none py-1 pl-3 pr-9 data-[focus]:bg-gray-50 border-t border-gray-100"
            >
              <span className="block truncate text-sm text-dataops-primary-blue font-medium">
                Add: &ldquo;{queryTrimmed}&rdquo;
              </span>
            </ComboboxOption>
          )}

          {/* Empty state */}
          {filteredOptions.length === 0 && !showAddOption && (
            <div className="relative cursor-default select-none py-2 px-4 text-sm text-gray-500">
              No options match your search.
            </div>
          )}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
}
